import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Trash2 } from 'lucide-react';
import Card from '../Card';
import { AppDispatch, RootState } from '../../store';
import {
  createSetupIntent,
  fetchPaymentMethods,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from '../../store/features/billing/billingSlice';

const panelBackground =
  'linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96))';
const panelBorder = 'rgba(255,255,255,.06)';
const panelText = '#E8EAED';
const panelMuted = 'rgba(186, 198, 224, 0.82)';
const panelSoft = 'rgba(186, 198, 224, 0.64)';
const cardSurface = 'rgba(255,255,255,.03)';
const cardSurfaceHover = 'rgba(255,255,255,.045)';
const cardBorder = 'rgba(255,255,255,.08)';
const accent = 'rgba(224, 161, 94, 0.88)';
const accentStrong = '#e0a15e';
const successTint = 'rgba(111, 214, 164, 0.18)';
const successText = '#bdf2d6';
const dangerText = '#ffb2ac';

const Panel = styled(Card)`
  background: ${panelBackground};
  border: 1px solid ${panelBorder};
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.28);
  color: ${panelText};
`;

const Header = styled.div`
  display: grid;
  gap: 0.4rem;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  color: ${panelText};
  font-weight: 900;
`;

const Muted = styled.p`
  margin: 0;
  color: ${panelMuted};
  font-size: 0.92rem;
  font-weight: 700;
`;

const Methods = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const MethodCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.65rem;
  align-items: center;
  min-width: 0;
  border: 1px solid ${cardBorder};
  border-radius: 16px;
  padding: 1rem 1.1rem;
  background: ${cardSurface};
  color: ${panelText};
  transition: border-color 120ms ease, transform 120ms ease, background 120ms ease;

  &:hover {
    border-color: ${accent};
    background: ${cardSurfaceHover};
    transform: translateY(-1px);
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

const EmptyState = styled(MethodCard)`
  grid-template-columns: 1fr;
  justify-items: start;
  gap: 0.35rem;
`;

const EmptyTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 900;
  color: ${panelText};
`;

const EmptyMeta = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: ${panelSoft};
`;

const MethodInfo = styled.div`
  display: grid;
  gap: 0.35rem;
  min-width: 0;
`;

const MethodName = styled.div`
  font-size: 0.98rem;
  font-weight: 900;
  color: ${panelText};
  letter-spacing: 0.01em;
`;

const MethodMeta = styled.div`
  color: ${panelMuted};
  font-size: 0.82rem;
  font-weight: 700;
`;
const FlatTextAction = styled.button`
  appearance: none;
  padding: 0;
  margin: 0.15rem 0 0;
  border: 0;
  background: transparent;
  color: ${accentStrong};
  font-size: 0.8rem;
  font-weight: 800;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #f0c490;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const DefaultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  color: ${successText};
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const RemoveIconButton = styled.button`
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${dangerText};
  cursor: pointer;
  transition: color 120ms ease, transform 120ms ease;

  &:hover:not(:disabled) {
    color: #ffd0cc;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.56;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButton = styled.button<{ $tone?: 'danger' }>`
  appearance: none;
  border: 1px solid
    ${({ $tone }) => ($tone === 'danger' ? 'rgba(255, 130, 120, 0.28)' : 'rgba(255,255,255,.12)')};
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === 'danger' ? 'rgba(120, 28, 24, 0.2)' : 'rgba(255,255,255,.04)'};
  color: ${({ $tone }) => ($tone === 'danger' ? dangerText : panelText)};
  padding: 0.55rem 0.95rem;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, transform 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${({ $tone }) => ($tone === 'danger' ? '#ff8a7d' : accentStrong)};
    background: ${({ $tone }) =>
      $tone === 'danger' ? 'rgba(120, 28, 24, 0.28)' : 'rgba(255,255,255,.07)'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.56;
    cursor: not-allowed;
    transform: none;
  }
`;

const PrimaryButton = styled(ActionButton)`
  border-color: rgba(224, 161, 94, 0.28);
  background: linear-gradient(180deg, rgba(58, 42, 24, 0.96), rgba(46, 34, 20, 0.96));
  color: #f8efe4;

  &:hover:not(:disabled) {
    border-color: ${accentStrong};
    background: linear-gradient(180deg, rgba(68, 48, 26, 0.98), rgba(52, 38, 20, 0.98));
  }
`;
const AddPaymentMethodButton = styled.button`
  appearance: none;
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  border: 1px dashed rgba(224, 161, 94, 0.48);
  background: transparent;
  color: ${accentStrong};
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease, background 120ms ease;

  &:hover:not(:disabled) {
    border-color: #f0c490;
    color: #f0c490;
    background: rgba(255,255,255,.03);
  }

  &:disabled {
    opacity: 0.56;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const AddCardPanel = styled.div`
  margin-top: 0.9rem;
  padding: 1rem;
  border-radius: 16px;
  background: rgba(255,255,255,.03);
  border: 1px solid ${cardBorder};
  box-shadow: inset 0 1px 0 rgba(255,255,255,.03);
`;

const AddCardTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 900;
  color: ${panelText};
  margin-bottom: 0.7rem;
`;

const CardInputShell = styled.div`
  padding: 0.95rem 1rem;
  border-radius: 14px;
  border: 1px solid ${cardBorder};
  background: rgba(255,255,255,.04);
  transition: border-color 120ms ease, background 120ms ease;

  &:focus-within {
    border-color: ${accent};
    background: rgba(255,255,255,.05);
  }
`;

const ErrorText = styled.div`
  color: #ff9f96;
  margin-top: 0.7rem;
  font-size: 0.83rem;
  font-weight: 700;
`;

export default function PaymentMethodsPanel() {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, paymentMethods } = useSelector((s: RootState) => s.billing);

  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    dispatch(fetchPaymentMethods());
  }, [dispatch]);

  const cardOptions = useMemo(
    () => ({
      hidePostalCode: true,
      style: {
        base: {
          color: '#E8EAED',
          fontSize: '16px',
          '::placeholder': { color: '#9AA4B2' },
        },
        invalid: { color: '#ff8080' },
      },
    }),
    []
  );

  const displayPaymentMethods = useMemo(() => {
    if (!Array.isArray(paymentMethods) || paymentMethods.length === 0) return [] as typeof paymentMethods;
    if (paymentMethods.length === 1 && !paymentMethods[0]?.isDefault) {
      return paymentMethods.map((method) => ({ ...method, isDefault: true }));
    }
    return paymentMethods;
  }, [paymentMethods]);

  async function handleAddCard() {
    setAddError(null);
    if (!stripe || !elements) return;

    try {
      setSaving(true);

      const clientSecret: string = await dispatch(createSetupIntent()).unwrap();
      if (!clientSecret) throw new Error('Missing clientSecret');

      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card element not found');

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (result.error) throw result.error;

      const methods = await dispatch(fetchPaymentMethods({ force: true })).unwrap();
      if (Array.isArray(methods) && methods.length > 0 && !methods.some((m) => m.isDefault)) {
        await dispatch(setDefaultPaymentMethod(methods[0].id));
      }

      setAdding(false);
    } catch (err: any) {
      setAddError(err?.message || 'Failed to add card');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Panel>
      <Header>
        <Title>Payment methods</Title>
      </Header>

      <Methods>
        {displayPaymentMethods.length === 0 && !loading && (
          <EmptyState>
            <EmptyTitle>No cards on file yet.</EmptyTitle>
            <EmptyMeta>Add a payment method to keep billing uninterrupted.</EmptyMeta>
          </EmptyState>
        )}

        {displayPaymentMethods.map((pm) => (
          <MethodCard key={pm.id}>
            <RemoveIconButton
              type="button"
              aria-label={`Remove ${pm.brand?.toUpperCase() || 'card'} ending in ${pm.last4}`}
              onClick={() => dispatch(removePaymentMethod(pm.id))}
            >
              <Trash2 size={16} />
            </RemoveIconButton>
            <MethodInfo>
              {pm.isDefault && <DefaultBadge>Default</DefaultBadge>}
              <MethodName>
                {pm.brand?.toUpperCase() || 'CARD'} **** {pm.last4}
              </MethodName>
              <MethodMeta>
                Expires {pm.exp_month}/{pm.exp_year}
              </MethodMeta>
              {!pm.isDefault && displayPaymentMethods.length > 1 && (
                <FlatTextAction onClick={() => dispatch(setDefaultPaymentMethod(pm.id))}>
                  make default
                </FlatTextAction>
              )}
            </MethodInfo>
          </MethodCard>
        ))}
      </Methods>

      {!adding ? (
        <Footer>
          <AddPaymentMethodButton onClick={() => setAdding(true)}>
            Add payment method
          </AddPaymentMethodButton>
        </Footer>
      ) : (
        <AddCardPanel>
          <AddCardTitle>Add card</AddCardTitle>
          <CardInputShell>
            <CardElement options={cardOptions} />
          </CardInputShell>
          {addError && <ErrorText>{addError}</ErrorText>}
          <Footer>
            <PrimaryButton onClick={handleAddCard} disabled={!stripe || !elements || saving}>
              {saving ? 'Saving...' : 'Save card'}
            </PrimaryButton>
            <ActionButton
              onClick={() => {
                setAdding(false);
                setAddError(null);
              }}
              disabled={saving}
            >
              Cancel
            </ActionButton>
          </Footer>
        </AddCardPanel>
      )}
    </Panel>
  );
}
